import type { UpcomingVisit } from '@/api/types/ProviderApi'
import { visitDayLine, visitTime } from '@/components/VisitDate'
import { MapWay } from '@/components/VisitPage/MapWay'
import { SitePassRow } from '@/components/VisitPage/SitePassRow'
import { StateChip, typeLine } from '@/components/VisitPage/StateChip'
import { VisitSlot } from '@/components/VisitPage/VisitSlot'
import { Glyph } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import { isLive, untilLabel } from '@/features/visits/checkIn'
import { showsPass, sitePassOf } from '@/features/visits/sitePass'
import { analytics } from '@/lib/analytics'
import { PhotoFade } from '@/ui/core/block'
import { CardGroundProvider } from '@/ui/core/card-ground'
import { Header } from '@/ui/core/header'
import { Pressable } from '@/ui/core/pressable'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { format, parseISO } from 'date-fns'
import { Image } from 'expo-image'
import { router } from 'expo-router'
import { View } from 'react-native'
import Images from '#/assets/images/image-map'

/**
 * The mouth and the jaw, not the eye: the crop the hero used, which is the furthest of the
 * reframings from where the booked screen crops the same photograph.
 */
const CROP = { left: '62%' as const, top: '58%' as const }

/**
 * Tall enough that the photograph is a photograph, and that the pill in one corner and three
 * lines of type in the other have air between them; short enough that the rows are still the card.
 *
 * Thirty-eight more points than it opened with, to carry the date at forty rather than
 * twenty-seven: the date is the card's subject and it was set smaller than the same date on the
 * page this card replaced, which was thirty. The display face comes in Medium only, so weight is
 * not a dial this type has and size is the whole of the emphasis.
 *
 * Forty fits because the date's width is fixed: `EEE, MMM d` is eleven characters whatever the
 * day — "TUE, SEP 29", "WED, OCT 7" — so there is no longer string waiting to wrap it.
 */
const PHOTO_HEIGHT = 188

/**
 * The photograph's mount: how much card shows round it on three sides.
 *
 * Twelve, and twelve is what decides its corner too — a shape nested inside a rounded one takes
 * the parent's radius less the inset, or the two curves stop being parallel and the eye reads the
 * inner shape as slightly wrong without being able to say why. The card is `rounded-note`, which
 * is twenty-four, so the picture is twelve.
 *
 * Twelve is the floor for this pair. At eight the mount reads as a border rather than a ground,
 * and the concentric rule pushes the inner radius to sixteen — which makes the photograph rounder
 * than the card holding it.
 */
/**
 * The lift a white card gets on the pink.
 *
 * The same shadow the Care score card wears, in the ground's own warm brown rather than a neutral
 * black: on a pink page a grey shadow reads as dirt under the card. Home only — the visit's own
 * page is the card, and a page does not float.
 */
const SHADOW = { boxShadow: '0 4px 10px rgba(120,62,58,0.10)' }

const MOUNT = 12
const PHOTO_RADIUS = 24 - MOUNT

/**
 * The air between the photograph and the first row, on top of the row's own twelve.
 *
 * Fourteen, which is what the rows are inset by — so the space above the list and the space
 * beside it are one measurement. Eight was right while the rows stood on a pink field of their
 * own; without it the address crowded the picture.
 */
const ROW_GAP = 14

/**
 * The mouth and the jaw, not the eye — the same crop the hero used, mounted in the card.
 *
 * The hero was three hundred points of photograph with a date on it and a chevron into a page.
 * Everything that page held is in the rows below this now, so the picture keeps the part of its
 * job nothing else can do — being a picture, with the date on it — and gives back the rest.
 *
 * Mounted rather than bled to the card's edges: with white showing round it and a corner of its
 * own it is an object *in* the card — the thing the brand owns — rather than the surface the card
 * is printed on. It was tried the other way, full-bleed behind everything, and once the rows
 * became opaque the card was paying for four hundred points of photograph to show a hundred and
 * eighty-eight of it.
 */
const Photo = ({ visit }: { visit: UpcomingVisit }) => {
	const now = new Date()
	const date = parseISO(visit.startTimeLocal)
	const today = visitDayLine(visit.startTimeLocal, now) === 'Today'
	const live = isLive(visit, now)

	return (
		<View
			style={{ height: PHOTO_HEIGHT, borderRadius: PHOTO_RADIUS, overflow: 'hidden' }}
		>
			<Image
				source={Images.BrandPhoto}
				style={{ width: '100%', height: '100%' }}
				contentFit="cover"
				contentPosition={CROP}
				transition={300}
				accessibilityLabel=""
			/>
			{/* Later and harder than a page's fade: a band is a third of a hero's height and the
			    type still has to stand on it. */}
			<PhotoFade from={0.04} to={0.78} start={0.35} />
			{/* The corner, as on every other card in the app: the state is a mark on the object,
			    not a label beside the date. On the photograph it takes the white ground — the
			    neutral grey is a card colour and disappears into a picture. */}
			<View className="absolute top-3 right-[14px]">
				<StateChip visit={visit} onPhoto />
			</View>
			{/* Fifteen from the picture's foot: at twelve the type line sat on its bottom edge,
			    and at eighteen it floated off it. Fourteen in from the sides, which is what the
			    rows below are inset by — so the date and the address stand on one line. */}
			<View className="absolute right-[14px] bottom-[15px] left-[14px]">
				<VStack gap={4}>
					{/* The same ladder whatever has happened: the date, or TODAY on the day.
					    It used to say CHECKED IN once a member was through the door, which is
					    what the chip in the corner says two inches away and what the row at the
					    foot says in the member's own words — and by then TODAY is not news but
					    the hour is. Nobody checks in for next week. */}
					<Header variant="h7" tone="white">
						{today ? 'Today' : 'Next visit'}
					</Header>
					{/* On the day the eyebrow has said the date, so the display line spends itself
					    on the hour and how long there is left. */}
					{/* Set under its own size, as this app's display type is everywhere: the h2
					    token is thirty-two on thirty. Two lines of a date want closing rather than
					    opening — they are one fact, not a paragraph. */}
					{/* A beat under the eyebrow: a small-caps label at eleven point sitting four
					    points off a forty-point line reads as part of it. */}
					<Header
						variant="h2"
						tone="white"
						className="text-[40px] leading-[37px]"
						style={{ marginTop: 4 }}
					>
						{today
							? `${visitTime(visit.startTimeLocal)}${
									live ? `\n${untilLabel(visit.startTimeUtc, now)}` : ''
							  }`
							: `${format(date, 'EEE, MMM d')}\n${visitTime(visit.startTimeLocal)}`}
					</Header>
					{/* "Take a seat" stood here once a member was checked in, which is the same
					    instruction as "We'll come and get you shortly" one row below it, in a
					    second voice. The block answers when; the row answers what is happening.
					*/}
					{/* What the visit is, on the picture with when it is.
					
					    It was a white line between the photograph and the rows, which made the
					    type a caption on the band rather than part of what the band says — and
					    left a strip of white doing nothing but holding it. */}
					{/* The type line and, on Home, the chevron that says the photograph is a
					    door. Home's card is not a way into the visit page today — nothing on that
					    screen is — so the walk and what-to-expect could not move to the page
					    without this. A chevron on the line rather than loose in the corner: the
					    corner is the state chip's, and a mark floating on a photograph reads as
					    decoration until it is set beside some words. */}
					<HStack className="items-end justify-between">
						<Text size="body1" tone="white" className="flex-1 opacity-80">
							{typeLine(visit)}
						</Text>
						<Glyph name="chevron" size={16} color={colors.white} />
					</HStack>
				</VStack>
			</View>
		</View>
	)
}

/**
 * The next visit, as Home's card: when, what, the way there, and the one thing to do about it.
 *
 * Home's card and the visit's own page were one component for a while, switched by a `variant`.
 * They have come apart, because they are answering different questions. This card is one of four
 * things on a scrolling screen and it holds what a member wants while walking — the photograph
 * with the date on it, the address, the code that opens the building, and the slot. The page is
 * `VisitFacts`, which leads with type because it has already been chosen.
 *
 * Two rows, at most. The walk was here and is on the page; what to expect was here for a day and
 * is a section on the page. Both were rows opening a sheet that held what that page shows, one
 * tap apart. What is left is the address and, inside somebody else's building, the pass — plus
 * the slot, which becomes a row of its own once a member is through the door.
 *
 * The photograph is the way to the page. Nothing on Home led there before, so the chevron on the
 * type line is load-bearing rather than decoration.
 */
export const VisitCardDetail = ({ visit }: { visit: UpcomingVisit }) => {
	const pass = showsPass(visit) ? sitePassOf(visit) : null

	return (
		/* The card carries the mount, so the photograph, the rows and the button all stand on one
		   inset and the picture is the only thing in here with a corner of its own. */
		<View
			className="overflow-hidden rounded-note bg-white"
			style={[{ padding: MOUNT }, SHADOW]}
		>
			<Pressable
				onPress={() => {
					analytics.trackClick('visit-open', { appointmentId: visit.appointmentId })
					router.push({
						pathname: '/visits/[id]',
						params: { id: String(visit.appointmentId) },
					})
				}}
				accessibilityRole="button"
				accessibilityLabel="See this appointment"
			>
				<Photo visit={visit} />
			</Pressable>
			{/* The rows stand on the card's own white, divided by the hairline each one carries —
			    which is how Billing sets its rows and how every sheet in this app sets a list. */}
			<CardGroundProvider ground="white">
				<View style={{ marginTop: ROW_GAP }}>
					<MapWay visit={visit} />
					{!!pass && <SitePassRow pass={pass} />}
					<VisitSlot visit={visit} />
				</View>
			</CardGroundProvider>
		</View>
	)
}
